import axios from "axios";
import generate from "./hash";
import cookie from "webmd-cookie";
import { getSLD } from "webmd-url";
import oauth from "webmd-oauth/src/index.es6";
import utils from "webmd-cms-store/src/store/helpers/utils.es6";
let api, legacyApi, urlHostName, isProdURL;

function getApi(host) {
    if (typeof window !== "undefined") {
        urlHostName = window.location.host.toLowerCase();
    } else if (host) {
        urlHostName = host;
    }

    isProdURL = urlHostName.search(
        /\.staging|\.feature|\.qa|localhost|\.devint|\.preview/
    ) === -1;

    if (isProdURL) {
        api = "https://www.webmd.com/api/svcgateway/v2/reg/regservice.asmx";
        legacyApi = "https://www.webmd.com/api/reg/regapi.svc/secure/json2";
    } else {
        api = "https://www.staging.webmd.com/api/svcgateway/v2/reg/regservice.asmx";
        legacyApi = "https://www.staging.webmd.com/api/reg/regapi.svc/secure/json2";
    }
}

// Store the subscribed newsletter ids in nlsub cookie
function setNLIDinCookie(nlId) {
    const nlsubCookieVal = cookie.get("nlsub");
    const oneYearTime = new Date(
        new Date().getTime() + (365 * 24 * 60 * 60 * 1000)
    );
    if (nlsubCookieVal.length > 0) {
        const updatedNlsubCookie = [
            ...new Set([...nlsubCookieVal.split(","), ...nlId.split(",")]),
        ].join(",");
        cookie.set("nlsub", updatedNlsubCookie, {
            expires: oneYearTime,
            domain: getSLD(),
        });
    } else {
        cookie.set("nlsub", nlId, { expires: oneYearTime, domain: getSLD() });
    }
}

async function generateReCaptchaToken() {
    let key;
    isProdURL = urlHostName.search(
        /\.feature|\.qa|localhost|\.devint/
    ) === -1;

    if (isProdURL) {
        key = "6LfHvYspAAAAAA1NxrDD0jWC8JyjXvvfRnwrPI_5";
    } else {
        key = "6LeCho0pAAAAABxCsEn52StHNJ4648kvzvO_weoS";
    }

    try {
        let token = await new Promise((resolve, reject) => {
            grecaptcha.enterprise.ready(async() => {
                try {
                    const result = await grecaptcha.enterprise.execute(key, {
                        action: "LOGIN",
                    });
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            });
        });

        return token;
    } catch (error) {
        console.error("Error generating reCAPTCHA token:", error);
        throw error;
    }
}

function validateEmail(email) {
    // Check if email is provided and not empty
    if (!email || typeof email !== 'string') {
      return false;
    }
  
    // Check max length of 100 characters
    if (email.length > 100) {
      return false;
    }
  
    // Email validation regex from requirements
    const emailRegex = /^([a-zA-Z0-9_-]+)(['#.a-zA-Z0-9_-]*)@(?!localhost|test|invalid|example)([a-zA-Z0-9_-]+)(.)([a-zA-Z0-9_-]+)([.][a-zA-Z0-9_-]+)$/;
  
    return emailRegex.test(email);
  }

let regService = {
    /**
     * Get userInfo provided a encrypted guid or using logged in user's cookie
     * @param EncGuid
     * @returns {object} user info object
     */
    getUserInfo: async(EncGuid) => {
        getApi();

        // If guid is passed, use getUserInfo endpoint to get user email address
        if (EncGuid) {
            try {
                let result = await axios.post(
                    `${api}/GetUserInfo`, { EncGuid }, { timeout: 5000, headers: generate(false) }
                );
                if (result && result.data) {
                    return result.data.EmailAddress;
                }

                return false;
            } catch (err) {
                return false;
            }

            // If guid is not passed, use user endpoint (that will use logged in cookie to get user info
        } else {
            try {
                let result = await axios.get(`${legacyApi}/user`, {
                    timeout: 3000,
                    headers: generate(true, true),
                });
                if (result && result.data) {
                    return result.data.data.userinfo[0].personField.emailAddressField;
                }

                return false;
            } catch (err) {
                return false;
            }
        }
    },

    /**
     * Get newsletter for given vendor ID
     * @param VendorId
     * @returns {Promise<boolean|any>}
     */
    getNewsletter: async(VendorId) => {
        getApi();

        try {
            let result = await axios.post(
                `${api}/GetNewsletters`, { VendorId }, { timeout: 5000, headers: generate(false) }
            );

            if (result && result.data) {
                return Object.assign({}, result.data, { VendorId });
            }

            return false;
        } catch (err) {
            return false;
        }
    },

    /**
     * Unsubscribes from one email given nlId and user's encrypted guid
     * @param NLID
     * @param EncGuid
     * @returns {Boolean}
     */
    unsubscribeFrom: async(NLID, EncGuid) => {
        getApi();

        try {
            let result = await axios.post(
                `${api}/UnsubscribeByEncGuid`, { NLID, EncGuid }, { timeout: 5000, headers: generate(false) }
            );

            if (result && result.data) {
                return result.data.code === 1;
            }

            return false;
        } catch (err) {
            return false;
        }
    },

    /**
     * Unsubscribes the user from all newsletter subscriptions given an encrypted guid
     * @param EncGuid
     * @returns {boolean}
     */
    unsubscribeAll: async(EncGuid) => {
        getApi();

        try {
            let result = await axios.post(
                `${api}/UnsubscribeAllByEncGuid`, { EncGuid }, { timeout: 5000, headers: generate(false) }
            );

            if (result && result.data) {
                return result.data.code === 1;
            }

            return false;
        } catch (err) {
            return false;
        }
    },

    /**
     * Subscribe to Newsletters
     * @param emailId
     * @param NLIDs
     * @returns {Boolean}
     */
    subscribetoNL: async(emailId, NLIDs) => {
        getApi();

        const recaptchaPresent =
            window.grecaptcha && window.grecaptcha.enterprise ? true : false;
        let headers = {};
        const apiEndpoint = recaptchaPresent ? "subscribe4" : "subscribe2";

        if (recaptchaPresent) {
            const reCaptchaToken = await generateReCaptchaToken();
            headers = {
                email: emailId,
                token: reCaptchaToken,
                nlids: NLIDs.toString(),
            };
        } else {
            headers = {
                NLRequest: {
                    NLIDS: NLIDs.toString(),
                },
                Email: emailId,
                PPAccept: 1,
            };
        }
        try {
            // Validate email before making API call
            const isValidEmail = validateEmail(emailId);
            if (!isValidEmail) {
                throw new Error("INVALID EMAIL");
            }
            let result = await axios.post(`${legacyApi}/${apiEndpoint}`, headers, {
                timeout: 5000,
                headers: generate(true),
            });

            if (result && result.data) {
                if(result.data.code === 0 && result.data.stat === "FAIL") {
                    throw new Error("INVALID EMAIL");
                }
                if (result.data.subscribekey) {
                    cookie.set("WBMD_MB9", result.data.subscribekey, {
                        expires: 270,
                        domain: getSLD(),
                    });
                }

                setNLIDinCookie(NLIDs.toString());

                if (result.data.hash) {
                    cookie.set("REG_UUID", result.data.hash.toLowerCase(), {
                        expires: 365 * 100,
                        domain: getSLD(),
                    });

                    /*
                     * Liveramp requested us to use the following snippet to fire a call to track
                     * https://jira.internetbrands.com/browse/PPE-346246
                     */
                    if (window && window.ats && window.ats.setAdditionalData) {
                        // This is to remove the envelope which is already available
                        window.localStorage.removeItem("_lr_env");
                        window.ats.setAdditionalData({
                            type: "emailHashes",
                            id: [result.data.hash.trim().toLowerCase()],
                        });
                    }

                    /*
                     * Calling the rpa method for Amazon publisher services if hash key is available
                     * jira.internetbrands.com/browse/PPE-356589
                     */
                    if (window && cookie.get("REG_UUID") !== "") {
                        window.tokenConfig = { optOut: false, hashedRecords: [] };
                        if (
                            window.OptanonActiveGroups &&
                            window.OptanonActiveGroups.indexOf("C0004") === -1
                        ) {
                            window.tokenConfig.optOut = true;
                        }
                        window.tokenConfig.hashedRecords = [
                            { type: "email", record: result.data.hash.toLowerCase() },
                        ];

                        // PPE-415505 - adding null check as this is breaking on sponsored pages
                        // causing metrics call to not fire
                        if (window.apstag) {
                            window.apstag.upa(window.tokenConfig, () => {
                                /* eslint-disable no-console */
                                console.log("Amazon token is updated");
                            });
                        }
                    }
                }
            }

            // Need to return the data object so that it can be used to further process data based on requirements
            if (result && result.data) {
                return result.data;
            }

            return {};
        } catch (err) {
            if (err.message === "INVALID EMAIL") {
                err.displayErr = "This email address appears to be invalid. Try different email address.";
                throw err;
            }
            return {};
        }
    },

    getNewsletterByChannel: async(channel, host) => {
        try {
            const oauthHeaders = oauth.generate();
            let result = await axios.get(
                `${utils.getEnvironment(host).servicedomain
                }/search/2/api/newsletterbychannelid?channelid=${channel}`, {
                    headers: {...oauthHeaders },
                }
            );

            if (result && result.data && result.data.data) {
                return result.data.data;
            }
        } catch (err) {
            console.warn("REG SERVICE ERROR::", err);
            return false;
        }

        return false;
    },

    getNewsletterById: async(id, host) => {
        try {
            const oauthHeaders = oauth.generate();
            let result = await axios.get(
                `${utils.getEnvironment(host).servicedomain
                }/search/2/api/newsletterbyid?newsletterid=${id}`, {
                    headers: {...oauthHeaders },
                }
            );

            if (result && result.data && result.data.data) {
                return result.data.data;
            }
        } catch (err) {
            console.warn("REG SERVICE ERROR::", err);
            return false;
        }

        return false;
    },
};

export {validateEmail};
export default regService;